import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  ConfigProvider,
  Form,
  FormInstance,
  Input,
} from "antd";
import { LoginRequestModel } from "api/repositories/authentications/model/LoginRequestModel";
import companyLogo from "assets/images/icon.png";
import { BaseViews, IBaseViews } from "components/base/BaseViews";
import { isEmpty } from "lodash";
import { createRef, ReactNode } from "react";
import { FaRegUser } from "react-icons/fa";
import { MdOutlineLockClock } from "react-icons/md";
import { ClassFormat } from "utils/format/ClassFormat";
import { colorFormat } from "utils/format/ColorFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { ILoginFeature, ILoginFeatureState } from "../interface/ILoginFeature";
import { LoginViewModel } from "../viewModel/LoginViewModel";
import { AuthenticationContext } from "context/AuthenticationContext";

export default class LoginFeature extends BaseViews<
  LoginViewModel,
  ILoginFeature,
  ILoginFeatureState
> {
  constructor(props: IBaseViews & ILoginFeature) {
    super(props);
    this.state = {
      loading: false,
      error: {},
      savedForNextLogin: true,
    };
    this.onSumbit = this.onSumbit.bind(this);
    this.onNavigate = this.onNavigate.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  private form = createRef<FormInstance>();

  protected override bindViewModel(): void {
    this.viewModel.input.loading
      .subscribe((res) => {
        this.setState({
          loading: res,
        });
      })
      .add(this.viewModel.subscriptions);

    this.viewModel.output.onLoginSuccess
      .subscribe(async (res) => {
        if (res === true) {
          await this.onNavigate();
        }
      })
      .add(this.viewModel.subscriptions);

    this.viewModel.output.onError
      .subscribe((res) => {
        if (!isEmpty(res)) {
          getMessage(res?.message, 3, "error");
        }
      })
      .add(this.viewModel.subscriptions);
  }

  private onChange() {
    this.setState({
      error: {},
    });
  }

  private async onNavigate() {
    const { navigate } = this.props;
    const { getRoles, user } = this.context;

    // await getRoles?.().then(() => navigate("/dashboard"));
    await getRoles?.();

    const isSupport =
      typeof user?.note === "string" && user.note.includes("isSupporter=true");

    navigate(isSupport ? "systems/review-sim" : "/dashboard");
  }

  private onSumbit(form: FormInstance<any>) {
    let bodies = {
      username: form?.getFieldValue("username"),
      password: form?.getFieldValue("password"),
      isSavedForNextLogin: this.state.savedForNextLogin,
    } as LoginRequestModel;
    this.viewModel.input.login.next(bodies);
  }

  render(): ReactNode {
    const { error, loading, savedForNextLogin } = this.state;
    return (
      <section className="w-full flex flex-col h-screen justify-start items-center">
        <div className="w-full xl:h-[110px] h-[80px] flex justify-start items-center border-b border-[#0000001A] shadow-md">
          <a
            className="flex xl:px-36 px-5 w-full"
            href="https://neci.vn/"
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={companyLogo}
              alt="company-logo"
              className="object-contain xl:w-[100px] w-[80px] xl:h-[100px] h-[80px]"
            />
          </a>
        </div>
        <div className="w-full h-full flex flex-col items-center justify-center gap-y-2 xl:px-0 px-2 login-ui">
          <h1 className="font-[600] xl:text-[25px] text-[20px]">
            {strings.GlobalLabels.Welcome}
          </h1>
          <div className="w-full xl:w-1/3 lg:w-1/2 md:w-2/3 sm:w-2/3 rounded-lg mx-4 border-[1px] border-[#FFFFFF] shadow-lg bg-white">
            <div className="m-auto lg:p-11 p-5">
              <Form
                ref={this.form}
                onFinish={() => this.onSumbit(this.form.current)}
              >
                <div className="flex justify-start items-center w-full gap-x-1">
                  <FaRegUser className={ClassFormat.inputIcon} />
                  <p className={ClassFormat.inputLabel}>
                    {strings.GlobalPlaceholder.UserName}
                  </p>
                </div>
                <div className="w-full">
                  <Form.Item
                    name="username"
                    className="h-18 pb-1 hover:scale-105 transition-all"
                    validateStatus={error.name ? "error" : undefined}
                    style={{ display: "inline" }}
                    rules={[
                      {
                        required: true,
                        type: "string",
                        message: strings.GlobalPlaceholder.UserName,
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      className={ClassFormat.loginInput}
                      placeholder={strings.GlobalPlaceholder.UserName}
                      onChange={this.onChange}
                    />
                  </Form.Item>
                </div>
                <div className="flex justify-start items-center w-full gap-x-1 mt-4">
                  <MdOutlineLockClock className={ClassFormat.inputIcon} />
                  <p className={ClassFormat.inputLabel}>
                    {strings.GlobalPlaceholder.Password}
                  </p>
                </div>
                <div className="w-full">
                  <Form.Item
                    name="password"
                    className="h-18 pb-4 hover:scale-105 transition-all"
                    validateStatus={error.password ? "error" : undefined}
                    style={{ display: "inline" }}
                    rules={[
                      {
                        required: true,
                        type: "string",
                        message: strings.GlobalPlaceholder.Password,
                      },
                    ]}
                  >
                    <>
                      <Input.Password
                        size="large"
                        className={ClassFormat.loginInput}
                        placeholder={strings.GlobalPlaceholder.Password}
                        iconRender={(visible) =>
                          visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                        }
                        onChange={this.onChange}
                      />
                    </>
                  </Form.Item>
                  <div className="w-full flex gap-2 mb-2 mt-5">
                    <Checkbox
                      value={savedForNextLogin}
                      onChange={(e) => {
                        this.setState({
                          savedForNextLogin: e.target.checked,
                        });
                      }}
                      defaultChecked={savedForNextLogin}
                    />
                    <p>{strings.GlobalMessage.SavingForLogin}</p>
                  </div>
                </div>
                <Form.Item style={{ display: "inline" }}>
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: colorFormat?.Blue,
                      },
                    }}
                  >
                    <Button
                      type="primary"
                      size="large"
                      className="font-semibold hover:scale-105 mt-3 transition-all w-full h-[50px]"
                      loading={loading}
                      htmlType="submit"
                    >
                      {strings.GlobalLabels.Login}
                    </Button>
                  </ConfigProvider>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </section>
    );
  }
}
